import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import i18n from 'i18next';
import truncate from 'lodash/truncate';
import delay from 'lodash/delay';

import { addToast, handleAxiosError } from 'core/notifications';
import axios from '@/util/axios';
import { getIsCoursePublishing, useOneShotSubscription, VersioningToolbar } from '@/exceed';

import getAxiosConfig from '../helpers/getAxiosConfig';
import handlePublish from '../helpers/handlePublish';

const VideoVersioningToolbar = ({ cache, cacheKey, cacheType }) => {
  const { data, isLoading, refetch } = cache;
  const {
    published_at: publishedAt,
    id,
    name,
    socket_channel: publishCompleteChannel,
    is_child_locale: isChildLocale,
    parent_locale_id: parentLocaleId,
  } = data || {};
  const isPublishing = getIsCoursePublishing(cache);
  const isArchived = publishedAt && !data.is_published;
  const { orgId, type } = useParams();
  const isInLiveMode = type === 'live';
  const history = useHistory();
  const queryClient = useQueryClient();

  // This must be delayed because the websocket message can be received by the client after the
  // course is published but before the publish job is marked complete and destroyed, and checking
  // for the absence of that job is the only reliable way to know that the publish completed
  // successfully.
  const publishedCallback = useCallback(
    (message) =>
      delay(
        ({ data: { result } }) => {
          const title = truncate(name, { length: 50 });

          if (result === 'success') {
            addToast(i18n.t('publish-success-toast', { title }), 'success');
          } else {
            addToast(i18n.t('publish-failure-toast', { title }), 'error');
          }
          refetch();
        },
        1000,
        message,
      ),
    [name],
  );

  useOneShotSubscription({
    shouldSubscribe: isPublishing,
    channel: publishCompleteChannel,
    callback: publishedCallback,
  });

  const updateVideoVersionStatus = async ({ action }) => {
    try {
      await axios(getAxiosConfig('update-video-version-status', { action, id, isInLiveMode }));
    } catch (error) {
      return handleAxiosError(error);
    }

    refetch();
  };

  const getLocaleUrl = (localeId) => {
    if (cacheType === 'topic') {
      return `/videos/organizations/${orgId}/${type}/topics/${id}/properties?version_id=${localeId}`;
    }

    const videoId = isChildLocale && isInLiveMode ? parentLocaleId : id;

    return `/videos/organizations/${orgId}/${type}/${videoId}/compose?version_id=${localeId}`;
  };

  const handleApproveClick = () => {
    updateVideoVersionStatus({ action: 'approve' });
  };

  const handleLocaleChange = ({ id: localeId }) => {
    history.push(getLocaleUrl(localeId));
  };

  const handleLocaleAdd = ({ id: localeId }) => {
    refetch();

    addToast(
      {
        button: {
          text: i18n.t('go-to-locale'),
          to: getLocaleUrl(localeId),
        },
        title: i18n.t('locale-added'),
      },
      'success',
    );
  };

  const handlePublishClick = (payload) => {
    queryClient.setQueryData(cacheKey, (oldProperties) => ({
      ...oldProperties,
      is_publishing: true,
    }));

    return handlePublish({ id, isInLiveMode, currentLocale: data.locale, ...payload });
  };

  const handleSubmitForReviewClick = () => {
    updateVideoVersionStatus({ action: 'submit_for_review' });
  };

  const handleUnlockClick = () => {
    updateVideoVersionStatus({ action: 'edit' });
  };

  const newLocaleAxiosConfigKey =
    cacheType === 'video' ? 'create-video-locale' : 'create-topic-locale';

  return (
    <VersioningToolbar
      getNewLocaleAxiosConfig={({ locale }) =>
        getAxiosConfig(newLocaleAxiosConfigKey, { id, isInLiveMode, locale, parentLocaleId })
      }
      hasUnpublishedChanges={data?.has_unpublished_changes}
      isLoading={isLoading}
      isLocaleSelectOnly={cacheType === 'topic'}
      isPublished={data?.is_published}
      isPublishing={isPublishing}
      isArchived={isArchived}
      locale={data?.locale}
      locales={data?.locales}
      onApproveClick={handleApproveClick}
      onLocaleAdd={handleLocaleAdd}
      onLocaleChange={handleLocaleChange}
      onPublishClick={handlePublishClick}
      onSubmitForReviewClick={handleSubmitForReviewClick}
      onUnlockClick={handleUnlockClick}
      publishData={{ id }}
      publishedAt={publishedAt}
      status={data?.state}
      updatedAt={data?.version_state_updated_at}
    />
  );
};

VideoVersioningToolbar.propTypes = {
  cache: PropTypes.shape({}).isRequired,
  cacheKey: PropTypes.arrayOf(PropTypes.string).isRequired,
  cacheType: PropTypes.oneOf(['topic', 'video']).isRequired,
};

export default VideoVersioningToolbar;
